import { Button, ButtonSizes, ButtonVariants, Spinner } from "@src/components";
import { FEES_ERROR_RATIO } from "@src/config";
import { useSwapContext, usePrivySmartWallet } from "@src/contexts";
import { useTokenBalance } from "@src/hooks";
import { RoutePath, SwapMode } from "@src/interfaces";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Button = {
  content: ReactNode;
  fn: () => void;
  disabled: boolean;
};

export const SwapButton = () => {
  const { smartWalletAddress, showMfaEnrollmentModalIfNotEnrolled, login } =
    usePrivySmartWallet();

  const {
    error,
    chain,
    token,
    isFetchingRoute,
    cashAmount,
    tokenAmount,
    cashToken,
    estimatedTokenOutAmount,
    route,
    swapMode,
    executeSwapTransaction,
    setIsDepositAndBuyFlow,
    feesToTokenValueRatio,
    acceptedHighFees,
    isFetchingChains,
  } = useSwapContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [mfaEnrollmentRequested, setMfaEnrollmentRequested] = useState(false);
  const chainId = useMemo(() => chain?.chainId, [chain?.chainId]);
  const tokenAddress = useMemo(() => token?.address, [token?.address]);
  const cashBalances = useTokenBalance({ token: cashToken });
  const tokenBalances = useTokenBalance({ token });
  const refreshRouteAfterLoginAndSwap = useRef(false);

  const makeSwap = useCallback(() => {
    if ((swapMode === SwapMode.Buy ? cashAmount : tokenAmount) && route) {
      if (
        swapMode === SwapMode.Buy &&
        Number(cashAmount) > Number(cashBalances.humanReadable)
      ) {
        setIsDepositAndBuyFlow(true);
        navigate(RoutePath.DepositLackingFunds);
      } else {
        setIsDepositAndBuyFlow(false);
        if (!mfaEnrollmentRequested) {
          if (showMfaEnrollmentModalIfNotEnrolled()) {
            setMfaEnrollmentRequested(true);
            return;
          }
        }

        executeSwapTransaction();
      }
    }
  }, [
    cashAmount,
    tokenAmount,
    swapMode,
    cashBalances,
    route,
    mfaEnrollmentRequested,
    showMfaEnrollmentModalIfNotEnrolled,
    navigate,
    executeSwapTransaction,
    setIsDepositAndBuyFlow,
  ]);

  // This useEffect handles a case where user gets quote and then logs in,
  // so we need to wait for the route to be fetched with a correct receiver address
  useEffect(() => {
    if (
      refreshRouteAfterLoginAndSwap.current &&
      route &&
      smartWalletAddress &&
      route?.receiver.toLowerCase() === smartWalletAddress?.toLowerCase()
    ) {
      makeSwap();
      refreshRouteAfterLoginAndSwap.current = false;
    }
  }, [makeSwap, route, smartWalletAddress]);

  const button: Button = useMemo(() => {
    if (isFetchingChains) {
      return {
        content: (
          <div className="flex justify-center items-center gap-2">
            <div>Loading</div>
            <Spinner className="w-4 h-4 fill-white" />
          </div>
        ),
        fn: () => {},
        disabled: true,
      };
    }

    if (!chain || !token) {
      return {
        content: (
          <div className="flex justify-center items-center gap-2">
            <div>Pick token</div>
          </div>
        ),
        fn: () => {},
        disabled: true,
      };
    }

    if (error) {
      return {
        content: error,
        fn: () => {},
        disabled: true,
      };
    }

    if (!smartWalletAddress) {
      return {
        content: location.pathname === RoutePath.Sell ? "Sell" : "Buy",
        fn: async () => {
          await login();
          refreshRouteAfterLoginAndSwap.current = true;
        },
        disabled: false,
      };
    }

    if (isFetchingRoute) {
      return {
        content: (
          <div className="flex justify-center items-center gap-2">
            <div>Loading</div>
            <Spinner className="w-4 h-4 fill-white" />
          </div>
        ),
        fn: () => {},
        disabled: true,
      };
    }

    if (feesToTokenValueRatio >= FEES_ERROR_RATIO && !acceptedHighFees) {
      return {
        content: "Confirm transaction",
        fn: () => {},
        disabled: true,
      };
    }

    if (
      location.pathname === RoutePath.Sell &&
      Number(tokenBalances.humanReadable) < Number(tokenAmount)
    ) {
      return {
        content: `Not enough ${token?.symbol || "tokens"}`,
        fn: () => {},
        disabled: true,
      };
    }

    if (
      !(location.pathname === RoutePath.Sell ? tokenAmount : cashAmount) ||
      !estimatedTokenOutAmount ||
      !route
    ) {
      return {
        content: location.pathname === RoutePath.Sell ? "Sell" : "Buy",
        fn: () => {},
        disabled: true,
      };
    }
    return {
      content: location.pathname === RoutePath.Sell ? "Sell" : "Buy",
      fn: () => makeSwap(),
      disabled: false,
    };
  }, [
    error,
    smartWalletAddress,
    isFetchingRoute,
    login,
    location.pathname,
    cashAmount,
    estimatedTokenOutAmount,
    route,
    tokenAmount,
    makeSwap,
    token,
    tokenBalances,
    feesToTokenValueRatio,
    acceptedHighFees,
    chain,
    isFetchingChains,
  ]);

  return (
    <Button
      variant={ButtonVariants.Primary}
      disabled={button.disabled}
      onClick={button.fn}
      size={ButtonSizes.Large}
    >
      {button.content}
    </Button>
  );
};
